import { useState } from 'react'
import moment from 'moment'

import { cookbookError, getCookbookQuote, US_STATES } from '../../../../api/cookbook'

import styles from './PriceBox.module.css'

const dollars = (n) => `$${Number(n).toFixed(2)}`

const delivery = ({ delivery_from: from, delivery_to: to }) => {
  if (!from || !to) return ''
  const a = moment(from)
  const b = moment(to)
  return a.isSame(b, 'day')
    ? `Arrives ${a.format('MMM D')}`
    : `Arrives ${a.format('MMM D')} to ${b.format('MMM D')}`
}

// Real prices from the printer for the built book (US addresses)
const PriceBox = ({ orderId }) => {
  const [form, setForm] = useState({
    country_code: 'US',
    state_code: '',
    postcode: '',
    quantity: 1,
  })
  const [quote, setQuote] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })

  const onSubmit = (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    getCookbookQuote(orderId, {
      ...form,
      country_code: 'US',
      quantity: Number(form.quantity),
    })
      .then(({ data }) => setQuote(data))
      .catch((err) => {
        setQuote(null)
        setError(cookbookError(err, 'We could not get prices right now. Please try again.'))
      })
      .finally(() => setLoading(false))
  }

  return (
    <section className="cbSection">
      <h2 className="cbSectionTitle">What would it cost?</h2>
      <p className="cbHint">
        See the price for this exact book, printed and shipped to you. We ship within the United
        States.
      </p>
      <form className={styles.form} onSubmit={onSubmit}>
        <label>
          State
          <select className="cbInput" value={form.state_code} onChange={set('state_code')} required>
            <option value="">Choose…</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          ZIP code
          <input
            className="cbInput"
            value={form.postcode}
            onChange={set('postcode')}
            inputMode="numeric"
            maxLength={12}
            required
          />
        </label>
        <label>
          Copies
          <select className="cbInput" value={form.quantity} onChange={set('quantity')}>
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="cbSecondaryButton" disabled={loading}>
          {loading ? 'Checking…' : 'See prices'}
        </button>
      </form>

      {error ? <p className="cbError">{error}</p> : null}

      {quote ? (
        <div className={styles.result}>
          <p className={styles.summary}>
            {quote.product.name} ({quote.product.binding.toLowerCase()}
            {quote.product.size_label ? `, ${quote.product.size_label}` : ''}), {quote.page_count}{' '}
            pages, {quote.quantity} {quote.quantity === 1 ? 'copy' : 'copies'}
          </p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Delivery</th>
                <th>Book{quote.quantity > 1 ? 's' : ''}</th>
                <th>Shipping</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.options.map((o) => (
                <tr key={o.level}>
                  <td>
                    <b>{o.name}</b>
                    <span>{delivery(o)}</span>
                  </td>
                  <td>
                    {dollars(o.book_price)}
                    {quote.quantity > 1 ? <span>{dollars(o.per_copy)} each</span> : null}
                  </td>
                  <td>{dollars(o.shipping)}</td>
                  <td className={styles.total}>{dollars(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cbMuted">{quote.tax_note}, added at payment. Prices in US dollars.</p>
        </div>
      ) : null}
    </section>
  )
}

export default PriceBox
